/**
 * Audio feedback para escaneos (Web Audio API, sin archivos externos).
 *
 *   - `success`: beep agudo corto (880 Hz, 80ms) — serie aceptada
 *   - `error`:   beep grave largo (220 Hz, 250ms) — serie rechazada
 *
 * Mantiene el comportamiento del mockup.
 *
 * Autoplay: el navegador puede dejar el AudioContext en `suspended` (creado
 * sin interacción previa del usuario, o sin dispositivo de salida). Antes de
 * cada beep se pide `resume()`; como es asíncrono, el beep se programa igual
 * y suena en cuanto el contexto arranca. `probarSonido()` existe para que el
 * operador verifique el audio con un clic, que además es la interacción que
 * el navegador exige para habilitarlo.
 *
 * Se ignora silenciosamente si Web Audio no está disponible (tests, browsers
 * viejos): el flujo nunca depende del audio para funcionar.
 */

export type TipoSonido = 'success' | 'error';

interface Beep {
  frecuencia: number;
  duracionMs: number;
  /** Ganancia lineal 0–1. */
  volumen: number;
}

const SONIDOS: Record<TipoSonido, Beep> = {
  success: { frecuencia: 880, duracionMs: 80, volumen: 0.12 },
  error: { frecuencia: 220, duracionMs: 250, volumen: 0.18 },
};

let audioCtx: AudioContext | null = null;

function getAudioContext(): AudioContext | null {
  if (typeof window === 'undefined') return null;
  if (audioCtx) return audioCtx;
  const Ctor =
    window.AudioContext ??
    (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!Ctor) return null;
  audioCtx = new Ctor();
  return audioCtx;
}

/** Pide reanudar el contexto si no está corriendo. No espera el resultado. */
function reanudar(ctx: AudioContext): void {
  if (ctx.state === 'running') return;
  try {
    void ctx.resume().catch(() => undefined);
  } catch {
    // Contexto cerrado o browser sin resume(): el beep se programa igual.
  }
}

/** Programa un beep en la línea de tiempo del contexto. Devuelve cuándo termina. */
function programarBeep(ctx: AudioContext, tipo: TipoSonido, inicio: number): number {
  const b = SONIDOS[tipo];
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.connect(gain);
  gain.connect(ctx.destination);
  osc.frequency.value = b.frecuencia;
  gain.gain.value = b.volumen;
  const fin = inicio + b.duracionMs / 1000;
  osc.start(inicio);
  osc.stop(fin);
  return fin;
}

export function playScanSound(tipo: TipoSonido): void {
  const ctx = getAudioContext();
  if (!ctx) return;
  try {
    reanudar(ctx);
    programarBeep(ctx, tipo, ctx.currentTime);
  } catch {
    // Defensivo: el audio es nice-to-have, no crítico.
  }
}

/**
 * Reproduce los dos sonidos seguidos y reporta si el contexto quedó corriendo.
 * Pensado para un botón "Probar sonido". Devuelve `false` si Web Audio no está
 * disponible o si el contexto no arrancó, que en la práctica significa que el
 * navegador no tiene dónde reproducir (sin parlantes, dispositivo deshabilitado).
 */
export async function probarSonido(): Promise<boolean> {
  const ctx = getAudioContext();
  if (!ctx) return false;
  try {
    if (ctx.state !== 'running') await ctx.resume();
    const fin = programarBeep(ctx, 'success', ctx.currentTime + 0.05);
    programarBeep(ctx, 'error', fin + 0.25);
    return ctx.state === 'running';
  } catch {
    return false;
  }
}
